import { useState } from 'react';
import { BadgeCheck, Check, PenLine, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import type { DecisionInput, DecisionKind, DecisionView, DentistDetails } from '@/types';

/**
 * The sign-off, docked under the notes.
 *
 * The only part of the portal that produces a clinical record, and treated as
 * one. There is no account behind a review link, so identity is captured at the
 * moment of the decision - name and registration number - or the approval says
 * nothing about who gave it. The decision is recorded once; after that the dock
 * reports it instead of asking.
 *
 * Choosing an option opens the form rather than submitting. The typed name is
 * the confirmation: it takes deliberate action, and a second "are you sure"
 * modal on top only trains people to click through both.
 */
const OPTIONS: {
  value: DecisionKind;
  label: string;
  verb: string;
  hint: string;
  icon: typeof Check;
  tone: string;
  solid: string;
}[] = [
  {
    value: 'approve',
    label: 'Approve',
    verb: 'Send approval',
    hint: 'The lab mills it as it is.',
    icon: Check,
    tone: 'border-success-500/30 text-success-600 hover:bg-success-50',
    solid: 'bg-success-500 hover:bg-success-600 text-white',
  },
  {
    value: 'changes_requested',
    label: 'Changes',
    verb: 'Send change request',
    hint: 'The lab revises it and sends you a new version.',
    icon: PenLine,
    tone: 'border-warning-500/30 text-warning-600 hover:bg-warning-50',
    solid: 'bg-warning-500 hover:bg-warning-600 text-white',
  },
  {
    value: 'reject',
    label: 'Reject',
    verb: 'Send rejection',
    hint: 'The design is not usable and is started again.',
    icon: X,
    tone: 'border-danger-500/30 text-danger-500 hover:bg-danger-50',
    solid: 'bg-danger-500 hover:bg-danger-600 text-white',
  },
];

const DECIDED_TONE: Record<DecisionKind, string> = {
  approve: 'bg-success-50 text-success-600',
  changes_requested: 'bg-warning-50 text-warning-600',
  reject: 'bg-danger-50 text-danger-600',
};

const DECIDED_LABEL: Record<DecisionKind, string> = {
  approve: 'Approved',
  changes_requested: 'Changes requested',
  reject: 'Rejected',
};

interface DecisionDockProps {
  existing: DecisionView | null;
  authorName: string;
  /** Details from this dentist's last review, pre-filled. */
  known: DentistDetails | null;
  /** How many added notes go with the decision. */
  draftCount: number;
  onDecide: (decision: Omit<DecisionInput, 'notes'>) => Promise<void>;
}

export function DecisionDock({
  existing,
  authorName,
  known,
  draftCount,
  onDecide,
}: DecisionDockProps) {
  const [choice, setChoice] = useState<DecisionKind | null>(null);
  const [name, setName] = useState('');
  const [clinic, setClinic] = useState(known?.clinic ?? '');
  const [registrationNo, setRegistrationNo] = useState(known?.registrationNo ?? '');
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);

  if (existing) {
    return (
      <div className="border-t border-border bg-background p-4">
        <div
          className={cn('flex items-start gap-3 rounded-lg p-3', DECIDED_TONE[existing.decision])}
        >
          <BadgeCheck className="mt-0.5 size-5 shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-semibold">{DECIDED_LABEL[existing.decision]}</p>
            <p className="text-[12px] opacity-90">
              {existing.deciderName}
              {existing.clinic && `, ${existing.clinic}`}
              {existing.registrationNo && ` · Reg. ${existing.registrationNo}`}
            </p>
            <p className="text-[11.5px] opacity-80">
              {new Date(existing.decidedAt).toLocaleString(undefined, {
                day: 'numeric',
                month: 'long',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </p>
            {existing.note && (
              <p className="mt-1.5 text-[12.5px] leading-relaxed">“{existing.note}”</p>
            )}
          </div>
        </div>
        <p className="mt-2 text-[11.5px] text-muted-foreground">
          Sent to the technician. To change it, ask the lab for a new version.
        </p>
      </div>
    );
  }

  const selected = OPTIONS.find((item) => item.value === choice) ?? null;
  const effectiveName = name || authorName;
  // A change or a rejection has to say why: in the note here, or in the notes
  // added above, which go with it.
  const needsNote = (choice === 'changes_requested' || choice === 'reject') && draftCount === 0;
  const ready =
    selected !== null &&
    effectiveName.trim().length > 0 &&
    registrationNo.trim().length > 0 &&
    (!needsNote || note.trim().length > 0) &&
    !sending;

  // Said under the button, so a greyed-out button always has a reason.
  const missing = !effectiveName.trim()
    ? 'Add your name to send this.'
    : !registrationNo.trim()
      ? 'Add your registration number to send this.'
      : needsNote && !note.trim()
        ? 'Add a note for the technician to send this.'
        : null;
  const withNotes = draftCount
    ? ` with ${String(draftCount)} note${draftCount === 1 ? '' : 's'}`
    : '';

  const submit = async () => {
    if (!ready) return;
    setSending(true);
    try {
      await onDecide({
        decision: selected.value,
        deciderName: effectiveName.trim(),
        clinic: clinic.trim(),
        registrationNo: registrationNo.trim(),
        note: note.trim(),
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="relative z-10 max-h-[65%] shrink-0 overflow-y-auto border-t border-border bg-background p-4 shadow-[0_-8px_20px_-16px_rgba(15,23,42,0.35)]">
      <div className="mb-2.5 flex items-center justify-between gap-2">
        <h2 className="text-[13px] font-semibold">Your decision</h2>
        {selected && (
          <div className="flex min-w-0 items-center gap-2">
            <span className="truncate text-[11.5px] text-muted-foreground">{selected.hint}</span>
            {/* Folds the form away; what was typed stays for when it reopens. */}
            <button
              type="button"
              aria-label="Close the decision form"
              title="Close"
              onClick={() => {
                setChoice(null);
              }}
              className="flex size-6 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <X className="size-3.5" />
            </button>
          </div>
        )}
      </div>

      <div role="radiogroup" aria-label="Decision" className="grid grid-cols-3 gap-2">
        {OPTIONS.map((option) => {
          const Icon = option.icon;
          const on = choice === option.value;
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={on}
              onClick={() => {
                setChoice(on ? null : option.value);
              }}
              className={cn(
                'flex h-10 items-center justify-center gap-1.5 rounded-lg border text-[13px] font-semibold transition-colors',
                on
                  ? `${option.solid} border-transparent shadow-sm`
                  : `bg-background ${option.tone}`,
              )}
            >
              <Icon className="size-4" />
              {option.label}
            </button>
          );
        })}
      </div>

      {selected && (
        <div className="mt-3 space-y-2.5">
          {known && (
            <p className="text-[11.5px] text-muted-foreground">
              Filled in from your last review. Change anything that is out of date.
            </p>
          )}
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label htmlFor="decider-name" className="text-[11.5px]">
                Name
              </Label>
              <Input
                id="decider-name"
                value={effectiveName}
                onChange={(event) => {
                  setName(event.target.value);
                }}
                placeholder="Dr A. Kulkarni"
                className="h-8 text-[13px]"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="decider-reg" className="text-[11.5px]">
                Registration no.
              </Label>
              <Input
                id="decider-reg"
                value={registrationNo}
                onChange={(event) => {
                  setRegistrationNo(event.target.value);
                }}
                placeholder="44-19827"
                className="h-8 font-mono text-[13px]"
              />
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="decider-clinic" className="text-[11.5px]">
              Clinic <span className="font-normal text-muted-foreground">(optional)</span>
            </Label>
            <Input
              id="decider-clinic"
              value={clinic}
              onChange={(event) => {
                setClinic(event.target.value);
              }}
              placeholder="Smile Studio, Pune"
              className="h-8 text-[13px]"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="decider-note" className="text-[11.5px]">
              {draftCount ? 'Anything else for the technician' : 'Note to the technician'}{' '}
              {!needsNote && <span className="font-normal text-muted-foreground">(optional)</span>}
            </Label>
            <textarea
              id="decider-note"
              rows={2}
              value={note}
              onChange={(event) => {
                setNote(event.target.value);
              }}
              placeholder={needsNote ? 'What needs to change?' : 'Anything the lab should know'}
              className="w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-[13px] outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/40"
            />
          </div>
          <Button
            onClick={() => void submit()}
            disabled={!ready}
            className={cn('w-full', selected.solid)}
          >
            {sending ? 'Sending…' : `${selected.verb}${withNotes}`}
          </Button>
          {missing && !sending && (
            <p className="text-center text-[11.5px] font-medium text-warning-600">{missing}</p>
          )}
          <p className="text-center text-[11px] text-muted-foreground">
            Recorded with your name and sent to the technician. It cannot be changed from this link.
          </p>
        </div>
      )}
    </div>
  );
}
